import { useEffect, useState } from "react";

import Api from "lib/api/httpRequestHelper";

const STORE_STORAGE_KEY = "vitrine-empresa-loja-selecionada";

function getStoredLoja() {
  try {
    const raw =
      typeof window !== "undefined"
        ? localStorage.getItem(STORE_STORAGE_KEY)
        : null;
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function setStoredLoja(idPessoaLoja) {
  try {
    if (typeof window === "undefined") return;
    if (idPessoaLoja == null) {
      localStorage.removeItem(STORE_STORAGE_KEY);
    } else {
      localStorage.setItem(STORE_STORAGE_KEY, String(idPessoaLoja));
    }
  } catch {
    // localStorage indisponível (modo privado, quota etc.) — seleção só em memória.
  }
}

/**
 * Loja (pessoa) do usuário vinculada ao pedido. As opções vêm restritas às lojas
 * cadastradas como cliente (VitrineVirtualB2bCliente) das vitrines que estão no carrinho
 * — evita mostrar lojas da estrutura comercial do usuário sem vínculo com essas vitrines,
 * o que forçaria o usuário a "chutar" a loja certa. Informativa quando há só uma loja
 * elegível; obrigatória quando há mais de uma.
 *
 * `selectedIdPessoaLoja`/`onSelectStore` são controlados pelo chamador (page.js), porque
 * o valor selecionado também precisa alimentar o `idPessoaLoja` do fetch da vitrine
 * (useVitrineData), que por sua vez determina os itens do carrinho usados aqui — evita
 * uma dependência circular entre os hooks.
 */
export default function useStoreSelection({
  idsVitrineNoCarrinho = [],
  selectedIdPessoaLoja,
  onSelectStore,
}) {
  const [requiresStoreSelection, setRequiresStoreSelection] = useState(false);
  const [singleStoreName, setSingleStoreName] = useState("");
  const [storeOptions, setStoreOptions] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  const idsVitrineKey = idsVitrineNoCarrinho.join(",");

  useEffect(() => {
    if (!idsVitrineKey) {
      setStoreOptions([]);
      setRequiresStoreSelection(false);
      setSingleStoreName("");
      onSelectStore(null);
      return undefined;
    }

    let active = true;
    (async () => {
      setLoadingStores(true);
      try {
        const response = await Api.get(
          "virtual-showcase-b2b-lojas-cliente",
          "showcaseB2B",
          `?idsVitrine=${idsVitrineKey}`,
          true,
        );
        if (!active) return;

        const lojas = (response?.Result ?? []).map((l) => ({
          idPessoaLoja: l.IdPessoaLoja,
          nomeLoja: l.NomeLoja || "",
          cnpjLoja: l.CnpjCpfLoja || "",
        }));
        setStoreOptions(lojas);

        if (lojas.length <= 1) {
          setRequiresStoreSelection(false);
          setSingleStoreName(lojas[0]?.nomeLoja || "");
          onSelectStore(lojas[0]?.idPessoaLoja ?? null);
          return;
        }

        setRequiresStoreSelection(true);
        setSingleStoreName("");
        const stored = getStoredLoja();
        const storedIsValid =
          stored != null && lojas.some((l) => l.idPessoaLoja === stored);
        onSelectStore(storedIsValid ? stored : null);
      } catch {
        if (active) setStoreOptions([]);
      } finally {
        if (active) setLoadingStores(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsVitrineKey]);

  const selectStore = (idPessoaLoja) => {
    onSelectStore(idPessoaLoja);
    setStoredLoja(idPessoaLoja);
  };

  return {
    selectedIdPessoaLoja,
    selectStore,
    requiresStoreSelection,
    singleStoreName,
    storeOptions,
    loadingStores,
  };
}
